import { ArrayBufferTarget, Muxer } from 'mp4-muxer';

// A VIDEO MADE FRAME BY FRAME, not recorded as it plays: each frame is drawn at the moment of the
// video it stands for and handed to the browser's encoder (WebCodecs) with that moment as its
// time, then packed into an MP4. A frame that takes long to draw — a scene still being built —
// costs the export time, never the video a frame: none is missed, none is late.
export const canExport = () => typeof VideoEncoder !== 'undefined' && typeof VideoFrame !== 'undefined';

export type FrameEncoder = { add: (canvas: HTMLCanvasElement, n: number) => Promise<void>; finish: () => Promise<Blob> };

export async function frameEncoder(width: number, height: number, fps: number): Promise<FrameEncoder> {
  const target = new ArrayBufferTarget();
  const muxer = new Muxer({ target, video: { codec: 'avc', width, height, frameRate: fps }, fastStart: 'in-memory' });
  let failed: unknown = null;
  const encoder = new VideoEncoder({
    output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
    error: (e) => { failed = e; },
  });
  // High profile, level 4.0: 1080p at 30. Main where High is not offered.
  const config: VideoEncoderConfig = { codec: 'avc1.640028', width, height, bitrate: 10_000_000, framerate: fps };
  if (!(await VideoEncoder.isConfigSupported(config)).supported) config.codec = 'avc1.4d0028';
  encoder.configure(config);
  return {
    add: async (canvas, n) => {
      if (failed) throw failed;
      const frame = new VideoFrame(canvas, { timestamp: Math.round((n * 1e6) / fps), duration: Math.round(1e6 / fps) });
      encoder.encode(frame, { keyFrame: n % (fps * 2) === 0 });
      frame.close();
      // NOT AHEAD OF THE ENCODER by more than a few frames: each one held is 8 MB.
      while (encoder.encodeQueueSize > 6) await new Promise((r) => setTimeout(r, 4));
    },
    finish: async () => {
      await encoder.flush();
      if (failed) throw failed;
      muxer.finalize();
      encoder.close();
      return new Blob([target.buffer], { type: 'video/mp4' });
    },
  };
}
